<template>
  <div class="seo-check">
    <h1 class="tit has-text-centered">
      Deixe sua página ainda melhor otimizando o SEO
    </h1>
    <ul class="list-items">
      <li
        v-for="(item, index) in listItems"
        :key="index"
        class="list-items__item"
        :class="{ valid: item.isValid }"
      >
        <div class="list-items__item__category">
          <div class="wrapper">
            <IconSuccess
              v-if="item.isValid"
              size="xs"
              class="valid"
            />
            <IconClose
              v-else
              size="xs"
              class="invalid"
            />
            <h2 class="title">{{ item.title }}</h2>
            <span class="description">
              {{ item.description }}
            </span>
          </div>
          <button
            class="button"
            @click="toggleActiveItem($event.target)"
          >
            {{ item.isValid ? 'Alterar' : 'Adicionar' }}
          </button>
        </div>

        <div class="list-items__item__content">
          <div class="wrapper">
            <div
              v-if="item.component.name === 'indexing'"
              class="indexing-active"
            >
              <h3>
                Deseja que a sua página esteja indexada nos mecanismos de busca?
              </h3>
            </div>
            <component
              :is="item.component.type"
              :name="item.component.name"
              :label="item.component.label"
              :help-text="item.component.helpText"
              :placeholder="item.component.placeholder"
              :count-char="item.component.countChar"
              :class="{ 'seo-image': item.component.name === 'image' }"
              :list-options="item.component.listOptions"
            />
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import IconSuccess from '@/components/icons/IconSuccess.vue';
import IconClose from '@/components/icons/IconClose.vue';
import HeadingSelector from '@/components/HeadingSelector.vue';

import ShortText from '@/ui-types/ShortText.vue';
import LongText from '@/ui-types/LongText.vue';
import ImageFile from '@/ui-types/ImageFile.vue';
import Checkbox from '@/ui-types/Checkbox.vue';

export default {
  name: 'SeoCheck',
  components: {
    IconSuccess,
    IconClose,
    ShortText,
    LongText,
    ImageFile,
    HeadingSelector,
    Checkbox,
  },

  computed: {
    listItems() {
      return [
        {
          title: 'Título SEO',
          description:
            'O título é o primeiro elemento que aparecerá na busca do Google, ele precisa explicar bem a sua página e ser convincente para que os usuários optem por acessar a sua página.',
          isValid: !!this.containerSettings.title,
          component: {
            type: ShortText,
            name: 'title',
            label: 'Insira o título que irá aparecer no Google',
            placeholder: 'Ex: O melhor hotel da américa latina',
            helpText: 'Tamanho recomendado de até 60 caracteres',
            countChar: true,
          },
        },
        {
          title: 'Descrição SEO',
          description:
            'A descrição irá aparecer no Google abaixo do título, para uma melhor otimização ela deve conter palavras-chave da página.',
          isValid: !!this.containerSettings.description,
          component: {
            type: LongText,
            name: 'description',
            label: 'Insira a descrição que irá aparecer no Google',
            placeholder: 'Ex: O melhor hotel da américa latina',
            helpText: 'Tamanho recomendado de até 160 caracteres',
            countChar: true,
          },
        },
        {
          title: 'Imagem para redes sociais',
          description:
            'A imagem aparece ao compartilhar o link da sua página via redes socias, como no WhatsApp e Facebook.',
          isValid: !!this.containerSettings.image,
          component: {
            type: ImageFile,
            name: 'image',
            helpText: 'Dimensão: 1200x627',
          },
        },
        {
          title: 'Título principal H1',
          description:
            'O título principal precisa conter as principais palavras chaves e explicar o que os usuários vão encontrar na sua página',
          isValid: !!this.containerSettings.headingId,
          component: {
            type: HeadingSelector,
            name: 'headingId',
            label:
              'Selecione 1 dos títulos da sua página para ser o seu título principal',
            listOptions: this.listOptions,
          },
        },
        {
          title: 'Mecanismos de busca',
          description:
            'Configure a visibilidade da sua página para os mecanismos de busca.',
          isValid: true,
          component: {
            type: Checkbox,
            name: 'indexing',
            label: 'Indexar e tornar a página visível nos mecanismos de busca',
            helpText:
              'Ao ativar essa opção, todos os mecanismos de busca, como Google e Bing, poderão ler e encontrar a sua página. Ao desativar, a página ainda ficará acessível, porém os mecanismos de busca não irão listá-la nos resultados. A página também será removida do sitemap do seu site.',
          },
        },
      ];
    },
    listOptions() {
      const options = [];

      this.activePage.instanceList.forEach((id) => {
        const instance = this.instancesThis[id];
        let titleEl = null;

        // Banner / BannerWithMenu
        if (
          instance.$options.name === 'Banner' ||
          instance.$options.name === 'BannerWithMenu'
        ) {
          const headerTitle = instance.$el.querySelector(
            '.header__content h1, .header__content h2',
          );
          const bannerTitle = instance.$el.querySelector(
            '.seo-banner h1, .seo-banner h2',
          );

          if (headerTitle) {
            options.push({
              label: headerTitle.innerText,
              id: `NewHeader-${id}`,
            });
          }
          if (bannerTitle) {
            options.push({
              label: bannerTitle.innerText,
              id: `Banner-${id}`,
            });
          }

          return;
        }

        // módulos gerais
        if (!titleEl) {
          titleEl = instance.$el.querySelector('h1, h2');
        }

        if (titleEl) {
          options.push({
            label: titleEl.innerText,
            id: `${instance.$options.name}-${id}`,
          });
        }
      });

      return options;
    },
    ...mapState(['instancesThis']),
    ...mapGetters(['containerSettings', 'activePage']),
  },
  methods: {
    toggleActiveItem(item) {
      item.parentNode.classList.toggle('active');
    },
  },
};
</script>

<style scoped lang="scss">
.seo-check {
  position: relative;
  width: 100%;
  border: 0;
  background: $gray-00;
  padding: 60px 120px;
  min-height: calc(100vh - 160px);

  .tit {
    margin-bottom: 48px;
    font-weight: $weight-bold;
    color: $gray-90;
  }

  .list-items {
    > li + li {
      margin-top: 20px;
    }

    &__item {
      border: 2px solid $orange-60;
      border-radius: 8px;

      &__category {
        width: 100%;
        padding: 26px 36px;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .wrapper {
          display: flex;
          align-items: center;
          justify-content: space-between;

          > * {
            margin-right: 30px;
          }
        }

        &.active {
          + .list-items__item__content {
            max-height: 800px;
          }
        }

        .icon {
          padding: 2px;
          flex-shrink: 0;
          border-radius: 100px;

          &.valid {
            color: $lb-white;
            background: $success-60;
            border: 2px solid $success-60;
          }

          &.invalid {
            color: $orange-100;
            border: 2px solid $orange-100;
          }
        }

        .title {
          white-space: nowrap;
          color: $gray-90;
        }

        .description {
          color: $gray-60;
        }

        .button {
          padding: 8px;
          outline: none;
          border-radius: 4px;
          color: $orange-100;
          background: $orange-40;
        }
      }

      &__content {
        max-height: 0;
        overflow: hidden;
        transition: all 0.3s ease-in-out;

        .wrapper {
          padding: 26px 36px;
          display: flex;
          flex-direction: column;

          .field {
            margin-bottom: 0;
          }

          .seo-image {
            max-width: 400px;
          }
        }
      }

      &.valid {
        border: 2px solid $gray-30;

        .list-items__item {
          &__category {
            .button {
              color: $primary-140;
              background: $primary-20;
            }
          }
        }
      }
    }
  }
  .indexing-active {
    h3 {
      font-weight: $lb-medium;
      font-size: $size-normal;
      line-height: 1.5;
      color: $dark;
    }
    + div {
      display: flex;
      flex-direction: column-reverse;
      ::v-deep {
        p {
          margin-left: -24px;
        }
      }
    }
  }
}
</style>
